import { useState } from 'react';
import logger from '../../utils/logger';

// Sous-hook Messagerie : création rapide de contact inconnu (client / courtier / annuaire) — iso-comportement
export function useMailQuickContacts({ backendUrl, authToken, loadEmails, setUnknownEmails, showSuccess, showError }) {
  const [showUnknownContactModal, setShowUnknownContactModal] = useState(false);
  const [unknownContactEmail, setUnknownContactEmail] = useState('');
  const [unknownContactStep, setUnknownContactStep] = useState(1); // 1: choix type, 2: formulaire
  const [unknownContactType, setUnknownContactType] = useState(null); // 'client', 'courtier', 'annuaire'
  const [creatingContact, setCreatingContact] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState({
    source: 'Client direct ASAR',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    type: 'Particulier',
    statut: 'Non actif (pas de contrat)',
    entreprise: '',
    notes: ''
  });
  const [quickCourtierForm, setQuickCourtierForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    orias: '',
    type: 'independant',
    specialite: '',
    experience: '',
    commentaires: ''
  });
  const [quickAnnuaireForm, setQuickAnnuaireForm] = useState({
    companyId: '',
    newCompanyName: '',
    isNewCompany: false,
    contactName: '',
    service: '',
    email: '',
    telephone: ''
  });
  const [companiesList, setCompaniesList] = useState([]);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/contact-directory/companies`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompaniesList(data);
      }
    } catch (error) {
      logger.error('Error loading companies:', error);
    }
  };

  // Ouvrir la modale de création de contact inconnu
  const openUnknownContactModal = (email) => {
    setUnknownContactEmail(email);
    setUnknownContactStep(1);
    setUnknownContactType(null);
    setQuickClientForm({
      source: 'Client direct ASAR',
      prenom: '',
      nom: '',
      email: email,
      telephone: '',
      type: 'Particulier',
      statut: 'Non actif (pas de contrat)',
      entreprise: '',
      notes: ''
    });
    setQuickCourtierForm({
      prenom: '',
      nom: '',
      email: email,
      telephone: '',
      orias: '',
      type: 'independant',
      specialite: '',
      experience: '',
      commentaires: ''
    });
    setQuickAnnuaireForm({
      companyId: '',
      newCompanyName: '',
      isNewCompany: false,
      contactName: '',
      service: '',
      email: email,
      telephone: ''
    });
    loadCompanies();
    setShowUnknownContactModal(true);
  };

  // Créer un client rapidement
  const createQuickClient = async () => {
    setCreatingContact(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prenom: quickClientForm.prenom,
          nom: quickClientForm.nom,
          email: quickClientForm.email,
          telephone: quickClientForm.telephone,
          type_client: quickClientForm.type,
          statut: quickClientForm.statut,
          entreprise: quickClientForm.entreprise,
          notes: quickClientForm.notes,
          source: quickClientForm.source
        })
      });

      if (response.ok) {
        showSuccess('Client créé avec succès !');
        // Retirer l'email de la liste des inconnus
        setUnknownEmails(prev => {
          const newSet = new Set(prev);
          newSet.delete(unknownContactEmail.toLowerCase());
          return newSet;
        });
        setShowUnknownContactModal(false);
        // Recharger la liste des emails pour mettre à jour les badges CRM
        if (loadEmails) loadEmails();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      logger.error('Error creating client:', error);
      showError('Erreur lors de la création du client');
    } finally {
      setCreatingContact(false);
    }
  };

  // Créer un courtier rapidement
  const createQuickCourtier = async () => {
    setCreatingContact(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/courtiers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: quickCourtierForm.prenom,
          last_name: quickCourtierForm.nom,
          email: quickCourtierForm.email,
          phone: quickCourtierForm.telephone,
          orias_number: quickCourtierForm.orias,
          type: quickCourtierForm.type.toLowerCase(),
          specialite: quickCourtierForm.specialite,
          experience: quickCourtierForm.experience,
          comments: quickCourtierForm.commentaires
        })
      });

      if (response.ok) {
        showSuccess('Courtier créé avec succès !');
        setUnknownEmails(prev => {
          const newSet = new Set(prev);
          newSet.delete(unknownContactEmail.toLowerCase());
          return newSet;
        });
        setShowUnknownContactModal(false);
        if (loadEmails) loadEmails();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      logger.error('Error creating courtier:', error);
      showError('Erreur lors de la création du courtier');
    } finally {
      setCreatingContact(false);
    }
  };

  // Créer un contact dans l'annuaire
  const createQuickAnnuaireContact = async () => {
    setCreatingContact(true);
    try {
      let companyId = quickAnnuaireForm.companyId;

      // Si nouvelle compagnie, la créer d'abord
      if (quickAnnuaireForm.isNewCompany && quickAnnuaireForm.newCompanyName) {
        const companyResponse = await fetch(`${backendUrl}/api/admin/contact-directory/companies`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: quickAnnuaireForm.newCompanyName,
            type: 'compagnie'
          })
        });

        if (companyResponse.ok) {
          const companyData = await companyResponse.json();
          companyId = companyData.id;
        } else {
          throw new Error('Erreur lors de la création de la compagnie');
        }
      }

      if (!companyId) {
        showError('Veuillez sélectionner ou créer une compagnie');
        setCreatingContact(false);
        return;
      }

      // Créer le contact
      const response = await fetch(`${backendUrl}/api/admin/contact-directory/companies/${companyId}/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: quickAnnuaireForm.contactName,
          service: quickAnnuaireForm.service,
          emails: [quickAnnuaireForm.email],
          phones: quickAnnuaireForm.telephone ? [quickAnnuaireForm.telephone] : []
        })
      });

      if (response.ok) {
        showSuccess('Contact ajouté à l\'annuaire !');
        setUnknownEmails(prev => {
          const newSet = new Set(prev);
          newSet.delete(unknownContactEmail.toLowerCase());
          return newSet;
        });
        setShowUnknownContactModal(false);
        if (loadEmails) loadEmails();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      logger.error('Error creating annuaire contact:', error);
      showError('Erreur lors de la création du contact');
    } finally {
      setCreatingContact(false);
    }
  };

  return {
    showUnknownContactModal, setShowUnknownContactModal,
    unknownContactEmail, setUnknownContactEmail,
    unknownContactStep, setUnknownContactStep,
    unknownContactType, setUnknownContactType,
    creatingContact, setCreatingContact,
    quickClientForm, setQuickClientForm,
    quickCourtierForm, setQuickCourtierForm,
    quickAnnuaireForm, setQuickAnnuaireForm,
    companiesList, setCompaniesList,
    loadCompanies, openUnknownContactModal,
    createQuickClient, createQuickCourtier, createQuickAnnuaireContact
  };
}
